import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { IMAGES } from '../../lib/images';

const portfolioImages = [
  ...IMAGES.portfolio,
  IMAGES.services.catering,
  IMAGES.services.rituals,
  IMAGES.locations.jaipur,
  IMAGES.locations.udaipur
];

export const Portfolio = () => {
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    if (selectedIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedIndex]);

  const openLightbox = (index) => {
    setSelectedIndex(index);
  };

  const closeLightbox = () => {
    setSelectedIndex(null);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev + 1) % portfolioImages.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev - 1 + portfolioImages.length) % portfolioImages.length);
  };

  return (
    <section id="portfolio" className="py-32 bg-background relative px-6 md:px-12 border-t border-border/50">
      <div className="max-w-7xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-secondary mb-6 font-medium">Chapter 04 — Our Work</div>
        <h2 className="text-5xl md:text-7xl font-editorial mb-16">The <span className="italic text-secondary">Gallery</span>.</h2>
        
        {/* Masonry Grid */}
        <div className="columns-1 sm:columns-2 md:columns-3 gap-6 space-y-6">
          {portfolioImages.map((src, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: (idx % 3) * 0.1, duration: 0.6 }}
              className="relative group overflow-hidden break-inside-avoid cursor-pointer"
              onClick={() => openLightbox(idx)}
            >
              <img 
                src={src} 
                alt={`Portfolio ${idx}`} 
                className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700" 
              />
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                <span className="text-white text-xs uppercase tracking-widest border border-white/50 px-6 py-3 rounded-full backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
                  Enlarge
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox Overlay */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button 
              onClick={closeLightbox} 
              className="absolute top-6 right-6 md:top-10 md:right-10 text-white/60 hover:text-white transition-colors z-50 p-2"
            >
              <X size={36} strokeWidth={1.5} />
            </button>
            
            {/* Prev Button */}
            <button 
              onClick={prevImage} 
              className="absolute left-4 md:left-10 text-white/40 hover:text-white transition-colors z-50 p-4"
            >
              <ChevronLeft size={48} strokeWidth={1} />
            </button>

            {/* Main Image */}
            <motion.img 
              key={selectedIndex}
              src={portfolioImages[selectedIndex]}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="max-w-[85vw] max-h-[85vh] object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Next Button */}
            <button 
              onClick={nextImage} 
              className="absolute right-4 md:right-10 text-white/40 hover:text-white transition-colors z-50 p-4"
            >
              <ChevronRight size={48} strokeWidth={1} />
            </button>
            
            {/* Image Counter */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 text-xs tracking-[0.3em] uppercase">
              {selectedIndex + 1} / {portfolioImages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
